import { Badge, Button, FormField, Input, Modal, useToast } from "@design";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  clearMyConnectorCredentialMutation,
  errorMessage,
  getConnectorDetailQueryKey,
  listConnectorsQueryKey,
  listMyConnectorConnectionsOptions,
  listMyConnectorConnectionsQueryKey,
  type MyConnectorConnection,
  setMyConnectorCredentialMutation,
} from "@/api";
import { PageSection, SettingsList, SettingsRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { useConnectorSignIn } from "@/features/build/connectors/useConnectorSignIn";

function TokenDialog({ connector, onClose }: Readonly<{ connector: MyConnectorConnection; onClose: () => void }>) {
  const { t } = useTranslation("settings");
  const toast = useToast();
  const queryClient = useQueryClient();
  const id = useId();
  const [value, setValue] = useState("");
  const save = useMutation(setMyConnectorCredentialMutation());
  // Modal re-runs its focus effect when onClose changes; a stable one keeps focus in the field.
  const pending = save.isPending;
  const close = useCallback(() => !pending && onClose(), [pending, onClose]);

  const submit = () =>
    save.mutate(
      { path: { connector_uuid: connector.uuid }, body: { value: value.trim() } },
      {
        onSuccess: async () => {
          await Promise.all(
            [
              listMyConnectorConnectionsQueryKey(),
              listConnectorsQueryKey(),
              getConnectorDetailQueryKey({ path: { connector_uuid: connector.uuid } }),
            ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
          );
          toast.success(t("connections.connectors.saved", { name: connector.name }));
          onClose();
        },
        onError: (error) => toast.error(errorMessage(error, t("connections.connectors.saveFailed"))),
      },
    );

  return (
    <Modal
      open
      onClose={close}
      title={t("connections.connectors.dialogTitle", { name: connector.name })}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            {t("connections.connectors.cancel")}
          </Button>
          <Button variant="primary" loading={pending} disabled={!value.trim()} onClick={submit}>
            {t("connections.connectors.save")}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (value.trim()) submit();
        }}
      >
        <Text variant="body-sm" tone="secondary">
          {t("connections.connectors.dialogBody")}
        </Text>
        <FormField label={t("connections.connectors.token")} htmlFor={id}>
          <Input id={id} type="password" autoComplete="off" value={value} onChange={(e) => setValue(e.target.value)} />
        </FormField>
      </form>
    </Modal>
  );
}

function ConnectorBadge({ connector }: Readonly<{ connector: MyConnectorConnection }>) {
  const { t } = useTranslation("settings");
  if (connector.disabledByAdmin) return <Badge variant="danger">{t("connections.connectors.disabled")}</Badge>;
  if (connector.connected) return <Badge variant="success">{t("connections.connected")}</Badge>;
  return null;
}

/**
 * Connectors I can use that take each person's own token (docs/design/web/WEB_EXTENSIONS_PLAN.md §6).
 * Hidden when there are none. The token is write-only: it's never shown again.
 */
export function ConnectorConnections() {
  const { t } = useTranslation("settings");
  const toast = useToast();
  const queryClient = useQueryClient();
  // An integration's own sign-in (Slack) is listed with the services above.
  const connectors = useQuery({
    ...listMyConnectorConnectionsOptions(),
    select: (r) => r.data.filter((c) => !c.integrationSlug),
  });
  const remove = useMutation(clearMyConnectorCredentialMutation());
  const [adding, setAdding] = useState<MyConnectorConnection | null>(null);
  const { signIn, pendingUuid } = useConnectorSignIn();
  const closeDialog = useCallback(() => setAdding(null), []);
  const rows = connectors.data ?? [];
  if (rows.length === 0) return null;

  const disconnect = (connector: MyConnectorConnection) =>
    remove.mutate(
      { path: { connector_uuid: connector.uuid } },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: listMyConnectorConnectionsQueryKey() });
          toast.success(t("connections.disconnected", { name: connector.name }));
        },
        onError: (error) => toast.error(errorMessage(error, t("connections.disconnectFailed"))),
      },
    );

  return (
    <PageSection title={t("connections.connectors.title")} description={t("connections.connectors.body")}>
      <SettingsList>
        {rows.map((connector) => (
          <SettingsRow
            key={connector.uuid}
            label={connector.name}
            description={connector.description ?? connector.url}
            badge={<ConnectorBadge connector={connector} />}
          >
            <div className="flex gap-2 md:justify-end">
              {connector.authMode === "oauth" ? (
                <Button
                  variant={connector.connected ? "secondary" : "primary"}
                  size="sm"
                  aria-label={t(
                    connector.connected
                      ? "connections.connectors.signInAgainNamed"
                      : "connections.connectors.signInNamed",
                    { name: connector.name },
                  )}
                  loading={pendingUuid === connector.uuid}
                  onClick={() => signIn(connector.uuid, "/settings/connections")}
                >
                  {t(connector.connected ? "connections.connectors.signInAgain" : "connections.connectors.signIn")}
                </Button>
              ) : (
                <Button
                  variant={connector.connected ? "secondary" : "primary"}
                  size="sm"
                  aria-label={t(
                    connector.connected ? "connections.connectors.replaceNamed" : "connections.connectors.addNamed",
                    { name: connector.name },
                  )}
                  onClick={() => setAdding(connector)}
                >
                  {t(connector.connected ? "connections.connectors.replace" : "connections.connectors.add")}
                </Button>
              )}
              {connector.connected && (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t("connections.connectors.disconnectNamed", { name: connector.name })}
                  loading={remove.isPending && remove.variables?.path.connector_uuid === connector.uuid}
                  onClick={() => disconnect(connector)}
                >
                  {t("connections.disconnect")}
                </Button>
              )}
            </div>
          </SettingsRow>
        ))}
      </SettingsList>
      {adding && <TokenDialog connector={adding} onClose={closeDialog} />}
    </PageSection>
  );
}
